import React, { Fragment, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { CenteredSpinner } from '@confluence/confluence-shared-components';
import { LoadableLazy } from '@confluence/loadable';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import {
	CONTENT_TYPES_HEADER_WATCH_EXPERIENCE,
	WATCH_DIALOG_WATCH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { ThirdPartyNudge } from '@confluence/third-party-nudge';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { WatchButton } from './WatchButton.js';
import { WATCH_BUTTON_METRIC } from './perf.config.js';
import { WATCH_DIALOG_LOCATION } from './WatchDialogLocation.js';
import { UpdateContentType, UpdateType } from './UpdateType.ts';

import WatchDialogWatchContentMutation from './__generated__/WatchDialogContentWatchContentMutation.graphql.ts';

export const LazyWatchDialogContent = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-WatchDialogContent" */ './WatchDialogContent.js'))
			.WatchDialogContent,

	loading: ({ shouldSSRWatchButton }) => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		<div style={{ width: '325px', height: '180px' }}>
			{!shouldSSRWatchButton && <CenteredSpinner size="medium" />}
		</div>
	),
});

const LazyWatchDialogShortcut = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-WatchDialogShortcut" */ './WatchDialogShortcut.js'
			)
		).WatchDialogShortcut,
});

const i18n = defineMessages({
	watchContentDialogLabel: {
		id: 'watch-dialog.popup.label',
		defaultMessage: 'Watch content dialog',
		description:
			'Label of the popup that contains content watching options used by assistive technologies',
	},
});

export const WatchDialogComponentWithWatchChildrenQuery = graphql`
	query WatchDialogComponentWithWatchChildrenQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			__typename
			id
			currentUserIsWatching
			currentUserIsWatchingChildren
			currentUserHasAncestorWatchingChildren
			type
			space {
				id
				key
				currentUser {
					isWatched
					isWatchingBlogs
				}
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

export const WatchDialogComponent = injectIntl(
	({
		contentId,
		spaceId,
		params,
		defaultTab,
		customPlacement,
		flagsContainer,
		/**
		 * This disables the usage of the watch button and dialog.
		 *
		 * Note: If a `customTrigger` is provided, this will be passed through
		 * as `isDisabled`. However, as a fallback, even if the button is not
		 * disabled properly, the logic here will continue to be disabled.
		 */
		isDisabled,
		customTrigger,
		useShortcut,
		shouldSSRWatchButton,
		skipTrialDetailsQuery,
		contentType,
		componentLocation,
		intl,
	}) => {
		const [dialogOpen, setDialogOpen] = useState(false);
		const [shouldOpenWatchDialogFromQueryParams, setShouldOpenWatchDialogFromQueryParams] =
			useState(false);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { getQueryParams, setQueryParams } = useContext(RoutesContext);
		const nudgeRef = useRef(null);
		const { isWatchingContent, isWatchingChildren, hasAncestorWatchingChildren, isWatchingSpace } =
			params;

		useEffect(() => {
			if (isDisabled) {
				return;
			}

			const { openWatchModal } = getQueryParams();

			setShouldOpenWatchDialogFromQueryParams(
				openWatchModal === 'true' &&
					(componentLocation === WATCH_DIALOG_LOCATION.SPACE_OVERVIEW ||
						componentLocation === WATCH_DIALOG_LOCATION.PAGE),
			);

			if (shouldOpenWatchDialogFromQueryParams) {
				setDialogOpen(true);
			}
		}, [isDisabled, getQueryParams, componentLocation, shouldOpenWatchDialogFromQueryParams]);

		const removeOpenModalQueryParams = () => {
			const queryParams = getQueryParams();
			if (queryParams.openWatchModal) {
				setQueryParams(
					{
						...queryParams,
						openWatchModal: undefined,
					},
					true,
				);
				setShouldOpenWatchDialogFromQueryParams(false);
			}
		};

		const toggleDialog = useCallback(() => {
			// Do not enable open the dialog if watch is disabled
			if (isDisabled) {
				return;
			}

			setDialogOpen((prevState) => !prevState);
		}, [isDisabled]);

		const isWatching = useCallback(() => {
			// If watch is disabled, then the user will default to not watching
			return isDisabled
				? false
				: isWatchingContent || isWatchingSpace || isWatchingChildren || hasAncestorWatchingChildren;
		}, [
			isWatchingContent,
			isWatchingChildren,
			hasAncestorWatchingChildren,
			isWatchingSpace,
			isDisabled,
		]);

		const updateQueryHandler = (updateContentType, newDataValue) => {
			const contentData = readQueryInterop({
				query: WatchDialogComponentWithWatchChildrenQuery,
				variables: { contentId },
			});

			switch (updateContentType) {
				case UpdateContentType.CONTENT:
					contentData.singleContent.currentUserIsWatching = newDataValue;
					break;
				case UpdateContentType.CHILDREN:
					contentData.singleContent.currentUserIsWatchingChildren = newDataValue;
					break;
				case UpdateContentType.SPACE:
					contentData.singleContent.space.currentUser.isWatched = newDataValue;
					break;
				default:
					contentData.singleContent.space.currentUser.isWatchingBlogs = newDataValue;
					break;
			}

			writeQueryInterop({
				query: WatchDialogComponentWithWatchChildrenQuery,
				data: contentData,
			});
		};

		const fireWatchAnalyticsEvent = useCallback(
			(action, actionSubject) => {
				const analyticsData = {
					action,
					actionSubject,
					actionSubjectId: contentId,
					objectId: contentId,
					objectType: contentType,
					source: 'WatchButton',
				};

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: analyticsData,
				}).fire();
			},
			[createAnalyticsEvent, contentId, contentType],
		);

		const [watchContent] = useMutationInterop(WatchDialogWatchContentMutation, {
			onCompleted: () => {
				fireWatchAnalyticsEvent(UpdateType.WATCH, UpdateContentType.CONTENT);
				toggleDialog();
				experienceTracker.succeed({
					name: WATCH_DIALOG_WATCH_EXPERIENCE,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: WATCH_DIALOG_WATCH_EXPERIENCE,
					error: error.message,
				});
			},
			update: (_, { data: { watchContent: watchContentData } }) => {
				updateQueryHandler(
					UpdateContentType.CONTENT,
					watchContentData.content.currentUserIsWatching,
				);
			},
		});

		const handleOneClickWatch = useCallback(async () => {
			const experienceState = experienceTracker.getExperienceState(WATCH_DIALOG_WATCH_EXPERIENCE);
			if (experienceState === null || experienceState.hasStopped) {
				experienceTracker.start({
					name: WATCH_DIALOG_WATCH_EXPERIENCE,
				});
			}

			await watchContent({ variables: { id: contentId } });
		}, [watchContent, contentId, experienceTracker]);

		const handleWatchButtonClick = useCallback(async () => {
			if (dialogOpen || isWatching()) {
				toggleDialog();
			} else {
				await handleOneClickWatch();
			}
		}, [toggleDialog, isWatching, dialogOpen, handleOneClickWatch]);

		const openDialog = () => {
			setDialogOpen(true);
		};

		const closeDialog = () => {
			setDialogOpen(false);
		};

		const renderPopupTrigger = (triggerProps) => {
			const TriggerButton = customTrigger || WatchButton;
			return (
				<>
					<TriggerButton
						isDisabled={isDisabled}
						isWatching={isWatching()}
						isSelected={dialogOpen}
						onMouseOver={LazyWatchDialogContent.preload}
						onClick={handleWatchButtonClick}
						triggerProps={triggerProps}
						shouldSSRWatchButton={shouldSSRWatchButton}
						defaultTab={defaultTab}
						contentId={contentId}
						updateQueryHandler={updateQueryHandler}
					/>
					<ExperienceSuccess
						name={CONTENT_TYPES_HEADER_WATCH_EXPERIENCE}
						attributes={{
							isWatching: isWatching(),
						}}
					/>
					<PageSegmentLoadEnd key={contentId} metric={WATCH_BUTTON_METRIC} />
				</>
			);
		};

		const renderPopupContent = () => {
			return (
				<>
					<div
						style={{
							padding: `${token('space.300')} ${token('space.200')}`,
						}}
						ref={nudgeRef}
					>
						<LazyWatchDialogContent
							contentId={contentId}
							spaceId={spaceId}
							defaultTab={defaultTab}
							params={params}
							closeWatchDialog={closeDialog}
							updateQueryHandler={updateQueryHandler}
							skipTrialDetailsQuery={skipTrialDetailsQuery}
							contentType={contentType}
							shouldOpenWatchDialogFromQueryParams={shouldOpenWatchDialogFromQueryParams}
							removeOpenModalQueryParams={removeOpenModalQueryParams}
						/>
					</div>
					{dialogOpen && (
						<ThirdPartyNudge
							trigger="watch-page"
							showImage={false}
							width={357}
							reference={nudgeRef}
						/>
					)}
				</>
			);
		};

		return (
			<Fragment>
				{useShortcut && !isDisabled && (
					<LazyWatchDialogShortcut
						contentId={contentId}
						contentType={contentType}
						params={params}
						flagsContainer={flagsContainer}
						openDialog={openDialog}
						updateQueryHandler={updateQueryHandler}
					/>
				)}
				<Popup
					isOpen={dialogOpen}
					onClose={closeDialog}
					content={() => renderPopupContent()}
					trigger={renderPopupTrigger}
					placement={customPlacement}
					role="dialog"
					label={intl.formatMessage(i18n.watchContentDialogLabel)}
					testId="watch-content-dialog"
					shouldRenderToParent
				/>
			</Fragment>
		);
	},
);

WatchDialogComponent.propTypes = {
	contentId: PropTypes.string.isRequired,
	spaceId: PropTypes.string,
	params: PropTypes.shape({
		isWatchingContent: PropTypes.bool.isRequired,
		isWatchingChildren: PropTypes.bool.isRequired,
		hasAncestorWatchingChildren: PropTypes.bool.isRequired,
		isWatchingSpace: PropTypes.bool.isRequired,
	}).isRequired,
	defaultTab: PropTypes.number.isRequired,
	customPlacement: PropTypes.string,
	flagsContainer: PropTypes.instanceOf(FlagsStateContainer),
	isDisabled: PropTypes.bool,
	customTrigger: PropTypes.elementType,
	useShortcut: PropTypes.bool,
	shouldSSRWatchButton: PropTypes.bool,
	skipTrialDetailsQuery: PropTypes.bool,
	contentType: PropTypes.string,
	componentLocation: PropTypes.number,
};
